import { useMemo } from 'react';
import { usePatients, useAppointments } from '@/services/api';

export interface SearchResult {
  type: 'patient' | 'appointment' | 'vet';
  id: string | number;
  title: string;
  subtitle: string;
  path: string;
}

export const useGlobalSearch = (query: string) => {
  const { data: patients = [] } = usePatients();
  const { data: appointments = [] } = useAppointments();

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];

    const lowerQuery = query.toLowerCase();
    const searchResults: SearchResult[] = [];

    // Search patients
    patients.forEach((patient) => {
      const matchName = patient.name?.toLowerCase().includes(lowerQuery);
      const matchOwner = patient.owner?.toLowerCase().includes(lowerQuery);
      const matchSpecies = patient.species?.toLowerCase().includes(lowerQuery);

      if (matchName || matchOwner || matchSpecies) {
        const queryParam = query.trim();
        searchResults.push({
          type: 'patient',
          id: patient.id,
          title: patient.name || 'Unknown',
          subtitle: `${patient.species || ''} | 飼主: ${patient.owner || 'Unknown'}`,
          path: `/patients?selected=${patient.id}${
            queryParam ? `&q=${encodeURIComponent(queryParam)}` : ''
          }`,
        });
      }
    });

    // Search appointments
    appointments.forEach((appointment) => {
      const matchPetName = appointment.petName?.toLowerCase().includes(lowerQuery);
      const matchOwner = appointment.owner?.toLowerCase().includes(lowerQuery);
      const matchVet = appointment.vetName?.toLowerCase().includes(lowerQuery);

      if (matchPetName || matchOwner || matchVet) {
        const date = new Date(appointment.date);
        const dateStr = date.toLocaleDateString('zh-TW', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        const queryParam = query.trim();

        searchResults.push({
          type: 'appointment',
          id: appointment.id,
          title: `${appointment.petName} - ${appointment.vetName}`,
          subtitle: `${dateStr} | ${appointment.status || '已排程'}`,
          path: `/appointments?selected=${appointment.id}${
            queryParam ? `&q=${encodeURIComponent(queryParam)}` : ''
          }`,
        });
      }
    });

    // Limit results to 8
    return searchResults.slice(0, 8);
  }, [query, patients, appointments]);

  return results;
};
